import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CategorizedFeed } from '@/components/CategorizedFeed';
import { apiService } from '@/services/api';
import { fetchZipcodeInfo } from '@/services/zipcode';
import { categorizeNews, sortLocalEvents, type CategorizedNews } from '@/services/gpt';
import styles from './FeedPage.module.css';

export type LoadingStep =
  | 'idle'
  | 'fetching'
  | 'scanning'
  | 'safety'
  | 'generating'
  | 'complete'
  | 'error';

function ZipcodeDisplay({ zipcode, locationName }: { zipcode: string; locationName: string }) {
  return (
    <div className={styles.location}>
      <span className={styles.zipcode}>{zipcode}</span>
      {locationName && (
        <span className={styles.locationName}>{locationName}</span>
      )}
    </div>
  );
}

export function FeedPage() {
  const { zipcode } = useParams<{ zipcode: string }>();
  const navigate = useNavigate();

  const [locationName, setLocationName] = useState<string>('');
  const [loadingStep, setLoadingStep] = useState<LoadingStep>('idle');
  const [articleCount, setArticleCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [categorizedData, setCategorizedData] = useState<CategorizedNews | null>(null);

  const fetchAndCategorize = useCallback(async (zip: string, cityName: string, abortSignal: AbortSignal) => {
    setLoadingStep('fetching');
    setError(null);
    setCategorizedData(null);

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    try {
      // Step 1: Fetching - minimum 0.7s for animation
      await delay(700);
      if (abortSignal.aborted) return;
      setLoadingStep('scanning');

      // Step 2: Scan and fetch news items
      const [items] = await Promise.all([
        apiService.fetchFeed(zip),
        delay(700),
      ]);
      if (abortSignal.aborted) return;
      setArticleCount(items.length);

      // Step 3: Safety - fetch crime news from police radio
      setLoadingStep('safety');
      const [safetyItems] = await Promise.all([
        apiService.fetchCrimeFeed(zip).catch(() => []),
        delay(700),
      ]);
      if (abortSignal.aborted) return;
      setArticleCount(items.length + safetyItems.length);

      if (items.length === 0 && safetyItems.length === 0) {
        setCategorizedData({
          safety: { points: [], otherItems: [] },
          urgent: { points: [], otherItems: [] },
          important: { points: [], otherItems: [] },
          informational: { points: [{ text: 'No news found for this area.', items: [] }], otherItems: [] },
        });
        setLoadingStep('complete');
        return;
      }

      // Step 4: Generate with GPT (this is the slow step)
      setLoadingStep('generating');

      // Run categorization and event sorting in parallel
      const [categorized, sortedEvents] = await Promise.all([
        categorizeNews(zip, cityName, items, safetyItems),
        sortLocalEvents(cityName, items).catch(() => null),  // Don't fail if event sorting fails
      ]);
      if (abortSignal.aborted) return;

      // Merge sorted events into categorized data
      const finalData: CategorizedNews = {
        ...categorized,
        events: sortedEvents || undefined,
      };

      setCategorizedData(finalData);
      setLoadingStep('complete');
    } catch (err) {
      if (abortSignal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch news');
      setLoadingStep('error');
    }
  }, []);

  // Reset scroll position on mount (iOS needs multiple approaches)
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (!zipcode || zipcode.length !== 5) {
      void navigate('/', { replace: true });
      return;
    }

    const abortController = new AbortController();

    // Fetch location name first, then fetch and categorize news
    const loadData = async () => {
      const info = await fetchZipcodeInfo(zipcode);
      if (abortController.signal.aborted) return;
      const cityName = info ? `${info.placeName}, ${info.stateAbbreviation}` : zipcode;
      setLocationName(cityName);
      await fetchAndCategorize(zipcode, cityName, abortController.signal);
    };

    void loadData();

    return () => {
      abortController.abort();
    };
  }, [zipcode, navigate, fetchAndCategorize]);

  const handleBack = useCallback(() => {
    void navigate('/');
  }, [navigate]);

  if (!zipcode) {
    return null;
  }

  const isLoading = loadingStep !== 'idle' && loadingStep !== 'complete' && loadingStep !== 'error';

  return (
    <div className={`${styles.page} ${isLoading ? styles.pageLoading : ''}`}>
      <header className={styles.header}>
        <button onClick={handleBack} className={styles.backButton}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          <span>Change Location</span>
        </button>
        <ZipcodeDisplay zipcode={zipcode} locationName={locationName} />
      </header>
      {error && <p className={styles.error}>Error: {error}</p>}
      <main className={styles.main}>
        <CategorizedFeed
          data={categorizedData}
          loadingStep={loadingStep}
          articleCount={articleCount}
          locationName={locationName}
        />
      </main>
    </div>
  );
}
